import React, { useState } from 'react';
import { toast } from 'sonner';
import { X as XIcon, RefreshCw, Minus, Plus, Phone, AlertTriangle } from 'lucide-react';
import { PorterAvatar, StatusPill, StaffingBadge, errMsg, withOverride } from './porterUi';
import { ReplacementPicker } from './ReplacementPicker';

export const CrewJobCard = ({ job, vans, statusMap, authFetch, reload, selectedPorter, onAssigned }) => {
  const [over, setOver] = useState(false);
  const [picker, setPicker] = useState(null);

  const assign = async (porterId, name) => {
    try {
      await withOverride((override_reason) => authFetch('post', '/admin/crew/assign', { porter_id: porterId, quote_id: job.id, override_reason }));
      toast.success(`${name} assigned to #${job.ref}.`);
      onAssigned?.();
      reload();
    } catch (e) { if (!e.cancelled) toast.error(errMsg(e)); }
  };
  const move = async (assignmentId, name, fromRef) => {
    if (!window.confirm(`Move ${name} from #${fromRef} to #${job.ref}?`)) return;
    try {
      await authFetch('post', `/admin/crew/assignments/${assignmentId}/move`, { quote_id: job.id });
      toast.success(`${name} moved to #${job.ref}.`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.porter.name} from #${job.ref}?`)) return;
    try { await authFetch('delete', `/admin/crew/assignments/${c.assignment.id}`); toast.success('Porter removed.'); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const patchJob = async (body) => {
    try { await authFetch('patch', `/admin/jobs/${job.id}/crew`, body); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    try {
      const d = JSON.parse(e.dataTransfer.getData('application/json'));
      if (d.assignmentId) { if (d.quoteId !== job.id) move(d.assignmentId, d.name, d.fromRef); }
      else assign(d.porterId, d.name);
    } catch { /* not a porter drag */ }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`min-w-0 rounded-lg border-2 p-3 bg-white transition-colors ${over ? 'border-[color:var(--mv-orange)] bg-orange-50' : job.staffing === 'full' ? 'border-emerald-200' : job.staffing === 'under' ? 'border-amber-300' : 'border-red-300'}`}
      data-testid={`crew-job-${job.ref}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-black text-[color:var(--mv-navy)]">{job.booking?.time} · #{job.ref}</div>
          <div className="text-xs text-slate-600 truncate">{job.name} · {job.from_postcode || '—'} → {job.to_postcode || '—'} · {job.hours_estimate || '2 hours'}</div>
        </div>
        <StaffingBadge job={job} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <select value={job.van?.id || ''} onChange={(e) => patchJob({ van_id: e.target.value || null })} className="h-9 rounded-md border border-slate-200 px-2 text-xs max-w-[200px]" data-testid={`crew-van-${job.ref}`}>
          <option value="">No van</option>
          {vans.map(v => <option key={v.id} value={v.id}>{v.reg}{v.name ? ` · ${v.name}` : ''}{v.off_road ? ' (OFF ROAD)' : ''}</option>)}
        </select>
        {job.van?.off_road && <span className="inline-flex items-center gap-1 text-red-700 font-bold" data-testid={`crew-van-offroad-${job.ref}`}><AlertTriangle className="w-3.5 h-3.5" />Van off road</span>}
        {job.van_inherited && <span className="text-slate-400">(driver's van today)</span>}
        <span className="ml-auto inline-flex items-center gap-1">
          Porters needed
          <button type="button" onClick={() => patchJob({ porters_required: Math.max(0, job.porters_required - 1) })} className="w-8 h-8 rounded border border-slate-200 inline-flex items-center justify-center" aria-label="Fewer porters" data-testid={`crew-req-minus-${job.ref}`}><Minus className="w-3 h-3" /></button>
          <strong className="w-5 text-center" data-testid={`crew-req-${job.ref}`}>{job.porters_required}</strong>
          <button type="button" onClick={() => patchJob({ porters_required: job.porters_required + 1 })} className="w-8 h-8 rounded border border-slate-200 inline-flex items-center justify-center" aria-label="More porters" data-testid={`crew-req-plus-${job.ref}`}><Plus className="w-3 h-3" /></button>
        </span>
      </div>
      <div className="mt-2 space-y-1.5">
        {job.crew.map(c => (
          <div
            key={c.assignment.id}
            draggable
            onDragStart={(e) => e.dataTransfer.setData('application/json', JSON.stringify({ assignmentId: c.assignment.id, quoteId: job.id, name: c.porter.name, fromRef: job.ref }))}
            className="flex flex-wrap sm:flex-nowrap items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5 cursor-grab"
            data-testid={`crew-member-${job.ref}-${c.porter.id}`}
          >
            <PorterAvatar p={c.porter} size={26} />
            <div className="flex-1 min-w-[120px]">
              <div className="text-sm font-bold text-[color:var(--mv-navy)] truncate">{c.porter.name}</div>
              {c.porter.phone && <a href={`tel:${c.porter.phone}`} className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.porter.phone}</a>}
            </div>
            <StatusPill status={statusMap?.[c.porter.id]} />
            <button type="button" onClick={() => setPicker(c)} className="h-8 px-2 rounded text-[11px] font-bold text-[color:var(--mv-orange)] inline-flex items-center gap-1" data-testid={`crew-replace-${job.ref}-${c.porter.id}`}><RefreshCw className="w-3 h-3" />Replace</button>
            <button type="button" onClick={() => remove(c)} className="w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-red-600" aria-label="Remove porter" data-testid={`crew-remove-${job.ref}-${c.porter.id}`}><XIcon className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {selectedPorter ? (
          <button type="button" onClick={() => assign(selectedPorter.id, selectedPorter.name)} className="h-10 px-3 rounded-md bg-[color:var(--mv-orange)] text-white text-xs font-bold" data-testid={`crew-assign-selected-${job.ref}`}>Assign {selectedPorter.name} here</button>
        ) : (
          <span className="text-[11px] text-slate-400 self-center hidden sm:inline">Drag a porter here</span>
        )}
        <button type="button" onClick={() => setPicker({})} className="h-10 px-3 rounded-md border border-slate-200 text-xs font-bold text-[color:var(--mv-navy)]" data-testid={`crew-find-${job.ref}`}>Find available porter</button>
      </div>
      {picker && <ReplacementPicker job={job} replacing={picker.assignment ? picker : null} authFetch={authFetch} onClose={() => setPicker(null)} onDone={reload} />}
    </div>
  );
};
